// Role Management Blueprint v2.3 · Phase 3 — Role & Permission Audit Log
// (registry-driven diff translation).
//
// Route: /admin/audit-log
//
// The audit feed still stores raw ``permissions[]`` snapshots (compliance-
// grade record — untouched). At render time each added / removed code is
// resolved back to its owning Business Capability via the codes map.
// Multiple codes belonging to a single Capability collapse into ONE
// business-language chip; any code with no Capability owner falls
// through to a neutral "Uncategorised access change" pill per Blueprint
// §7.4 / §9.
//
// Guardrails observed (Phase 3 scope):
//   * All existing filters (entity_type, action, actor_email, entity_id,
//     date range) preserved.
//   * CSV export unchanged — still streams the raw permission codes
//     from ``/admin/audit/list.csv`` for compliance systems.
//   * Row-count strip preserved.
//   * Historical rows referencing retired / renamed capabilities render
//     with the current business name; unmapped codes fall through.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, DownloadSimple, ArrowsClockwise, Funnel,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE, getToken } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";


function formatDateTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch {
        return iso;
    }
}

// Translate an added / removed permission-code set into a set of
// business-language chips. Codes are grouped by their owning capability
// so a single "Cancel issued invoices" chip stands in for the
// invoice:cancel code, etc. Codes belonging to no capability
// fall through as a single "Uncategorised access change" chip carrying
// the count of unmapped codes (never displays the codes themselves).
function translateCodes(codeSet, codesByCapability, capabilityMeta, ownerByCode) {
    if (!codeSet.size) return [];
    const remaining = new Set(codeSet);
    const chips = [];

    // Walk every capability that has all its codes represented in the
    // input set — collapse them into one chip.
    for (const [cid, codes] of Object.entries(codesByCapability)) {
        if (!codes.length) continue;
        if (codes.every((c) => remaining.has(c))) {
            const meta = capabilityMeta[cid];
            if (meta) {
                chips.push({
                    kind: "capability",
                    identifier: cid,
                    business_name: meta.business_name,
                    module_label: meta._module?.label || null,
                    risk_level: meta.risk_level || null,
                    capability_type: meta.capability_type || null,
                    danger: meta.flags?.danger || false,
                });
                codes.forEach((c) => remaining.delete(c));
            }
        }
    }

    // Some codes may belong to a capability but be a strict subset of
    // its saved codes. Show them as a partial-match capability chip
    // (with a "partial" annotation) so the auditor still gets business
    // meaning. Codes with no owning capability fall through.
    const orphan = [];
    for (const c of remaining) {
        const cid = ownerByCode[c];
        if (cid) {
            const meta = capabilityMeta[cid];
            if (meta) {
                chips.push({
                    kind: "capability",
                    identifier: cid,
                    business_name: meta.business_name,
                    module_label: meta._module?.label || null,
                    risk_level: meta.risk_level || null,
                    capability_type: meta.capability_type || null,
                    danger: meta.flags?.danger || false,
                    partial: true,
                });
                continue;
            }
        }
        orphan.push(c);
    }
    if (orphan.length) {
        chips.push({
            kind: "uncategorised",
            count: orphan.length,
        });
    }
    return chips;
}


export default function RoleAuditLog() {
    const [items, setItems] = useState([]);
    const [actions, setActions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        entity_type: "",
        action: "",
        actor_email: "",
        entity_id: "",
        date_from: "",
        date_to: "",
    });
    const [total, setTotal] = useState(0);
    const [codesByCapability, setCodesByCapability] = useState({});
    const [capabilityMeta, setCapabilityMeta] = useState({});
    const [ownerByCode, setOwnerByCode] = useState({});

    // Load registry once — reused for every row's diff translation.
    useEffect(() => {
        (async () => {
            try {
                const [taxResp, codesResp] = await Promise.all([
                    api.get("/admin/access/taxonomy"),
                    api.get("/admin/access/capability-codes-map"),
                ]);
                const flat = [];
                for (const m of taxResp.data.modules || []) {
                    for (const c of m.capabilities || []) flat.push({ ...c, _module: m });
                    for (const w of m.workflows || []) {
                        for (const c of w.capabilities || []) flat.push({ ...c, _module: m });
                        for (const f of w.functions || []) {
                            for (const c of f.capabilities || []) flat.push({ ...c, _module: m });
                        }
                    }
                }
                const meta = {};
                for (const c of flat) meta[c.capability_identifier] = c;
                setCapabilityMeta(meta);
                const codes = codesResp.data.codes_by_capability || {};
                setCodesByCapability(codes);
                const owner = {};
                for (const [cid, cs] of Object.entries(codes)) {
                    for (const code of cs) {
                        // First-writer-wins for shared codes (deterministic).
                        if (!owner[code]) owner[code] = cid;
                    }
                }
                setOwnerByCode(owner);
            } catch (err) {
                // Non-fatal — the diff will fall back to neutral pills.
                toast.error(formatApiError(err));
            }
        })();
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            params.limit = 200;
            const [r, a] = await Promise.all([
                api.get("/admin/audit/list", { params }),
                api.get("/admin/audit/actions"),
            ]);
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
            setActions(a.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { reload(); }, [reload]);

    const csvUrl = useMemo(() => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
        return `${API_BASE}/admin/audit/list.csv?${params.toString()}`;
    }, [filters]);

    const downloadCsv = async () => {
        try {
            const token = getToken();
            const resp = await fetch(csvUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) {
                toast.error(`CSV export failed (HTTP ${resp.status})`);
                return;
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const cd = resp.headers.get("Content-Disposition") || "";
            const m = cd.match(/filename="([^"]+)"/);
            link.download = m ? m[1] : "reedos-role-audit.csv";
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="space-y-6" data-testid="audit-log-page">
            <PageHeader
                title="Role & Access Audit Log"
                subtitle="Every role and access change is captured here. Compliance-grade trail with actor, before / after and timestamp."
                breadcrumbs={[
                    { label: "Administration" },
                    { label: "Roles", to: "/admin/roles" },
                    { label: "Audit Log" },
                ]}
                actions={
                    <div className="flex items-center gap-2">
                        <Link to="/admin/roles">
                            <Button variant="outline" data-testid="back-to-roles-btn">
                                <ArrowLeft className="w-4 h-4 mr-2" /> Back
                            </Button>
                        </Link>
                        <Button variant="outline" onClick={reload} data-testid="refresh-btn">
                            <ArrowsClockwise className="w-4 h-4 mr-2" /> Refresh
                        </Button>
                        <Button onClick={downloadCsv} data-testid="export-csv-btn">
                            <DownloadSimple className="w-4 h-4 mr-2" /> Export CSV
                        </Button>
                    </div>
                }
            />

            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="audit-filters">
                <div>
                    <Label className="text-xs">Entity type</Label>
                    <select
                        className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
                        value={filters.entity_type}
                        onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value }))}
                        data-testid="filter-entity-type"
                    >
                        <option value="">All (default: role+permission+user+employee)</option>
                        <option value="role">role</option>
                        <option value="permission">permission</option>
                        <option value="user">user</option>
                        <option value="employee">employee</option>
                        <option value="*">* — all entity types</option>
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Action</Label>
                    <select
                        className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
                        value={filters.action}
                        onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
                        data-testid="filter-action"
                    >
                        <option value="">All actions</option>
                        {actions.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Actor email</Label>
                    <Input
                        value={filters.actor_email}
                        onChange={(e) => setFilters((f) => ({ ...f, actor_email: e.target.value }))}
                        placeholder="substring..."
                        className="text-sm"
                        data-testid="filter-actor-email"
                    />
                </div>
                <div>
                    <Label className="text-xs">Entity ID (role code)</Label>
                    <Input
                        value={filters.entity_id}
                        onChange={(e) => setFilters((f) => ({ ...f, entity_id: e.target.value }))}
                        placeholder="e.g. developer"
                        className="text-sm"
                        data-testid="filter-entity-id"
                    />
                </div>
                <div>
                    <Label className="text-xs">From</Label>
                    <Input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                        className="text-sm"
                        data-testid="filter-date-from"
                    />
                </div>
                <div>
                    <Label className="text-xs">To</Label>
                    <Input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                        className="text-sm"
                        data-testid="filter-date-to"
                    />
                </div>
            </div>

            {/* Counts strip */}
            <div className="flex items-center gap-3 text-xs text-slate-500">
                <Funnel className="w-4 h-4" />
                <span data-testid="audit-result-count">Showing {items.length} of {total} events</span>
                {Object.values(filters).some((v) => !!v) && (
                    <button
                        type="button"
                        className="text-indigo-600 hover:underline"
                        onClick={() => setFilters({
                            entity_type: "", action: "", actor_email: "", entity_id: "", date_from: "", date_to: "",
                        })}
                        data-testid="clear-filters-btn"
                    >
                        Clear all filters
                    </button>
                )}
            </div>

            {loading ? (
                <div className="text-sm text-slate-500" data-testid="audit-loading">Loading...</div>
            ) : items.length === 0 ? (
                <EmptyState
                    title="No audit events match"
                    description="Try widening the date range or clearing the filters."
                />
            ) : (
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="audit-table">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-slate-600 text-xs uppercase tracking-wide">
                            <tr>
                                <th className="px-4 py-3">When</th>
                                <th className="px-4 py-3">Actor</th>
                                <th className="px-4 py-3">Action</th>
                                <th className="px-4 py-3">Entity</th>
                                <th className="px-4 py-3">Change</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {items.map((ev) => (
                                <AuditRow
                                    key={ev.id}
                                    ev={ev}
                                    codesByCapability={codesByCapability}
                                    capabilityMeta={capabilityMeta}
                                    ownerByCode={ownerByCode}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

function AuditRow({ ev, codesByCapability, capabilityMeta, ownerByCode }) {
    const oldPerms = new Set(ev.old_value?.permissions || []);
    const newPerms = new Set(ev.new_value?.permissions || []);
    const addedCodes = new Set();
    const removedCodes = new Set();
    newPerms.forEach((p) => { if (!oldPerms.has(p)) addedCodes.add(p); });
    oldPerms.forEach((p) => { if (!newPerms.has(p)) removedCodes.add(p); });

    const addedChips = translateCodes(addedCodes, codesByCapability, capabilityMeta, ownerByCode);
    const removedChips = translateCodes(removedCodes, codesByCapability, capabilityMeta, ownerByCode);

    const actionColor = ev.action?.endsWith("_create")
        ? "bg-emerald-100 text-emerald-700"
        : ev.action?.endsWith("_delete")
            ? "bg-rose-100 text-rose-700"
            : "bg-indigo-100 text-indigo-700";

    return (
        <tr className="hover:bg-slate-50 align-top" data-testid={`audit-row-${ev.id}`}>
            <td className="px-4 py-3 whitespace-nowrap text-xs font-mono text-slate-600">
                {formatDateTime(ev.created_at)}
            </td>
            <td className="px-4 py-3 text-xs">
                <div className="font-medium text-slate-900">{ev.actor_email}</div>
                <div className="text-slate-500">{ev.actor_role}</div>
            </td>
            <td className="px-4 py-3">
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium font-mono ${actionColor}`}>
                    {ev.action}
                </span>
            </td>
            <td className="px-4 py-3 text-xs">
                <div className="font-mono text-slate-700">{ev.entity_id}</div>
                <div className="text-slate-500">{ev.entity_type}</div>
            </td>
            <td className="px-4 py-3">
                <div className="text-xs space-y-1.5">
                    {ev.new_value?.name && ev.old_value?.name && ev.new_value.name !== ev.old_value.name && (
                        <div className="text-slate-700">
                            name: <span className="line-through text-slate-400">{ev.old_value.name}</span> → <strong>{ev.new_value.name}</strong>
                        </div>
                    )}
                    {addedChips.length > 0 && (
                        <ChipList
                            label={`+${addedCodes.size}`}
                            chips={addedChips}
                            tone="added"
                            testidPrefix={`audit-row-${ev.id}-added`}
                        />
                    )}
                    {removedChips.length > 0 && (
                        <ChipList
                            label={`−${removedCodes.size}`}
                            chips={removedChips}
                            tone="removed"
                            testidPrefix={`audit-row-${ev.id}-removed`}
                        />
                    )}
                    {ev.action === "role_create" && (
                        <div className="text-slate-500 italic">
                            created with {ev.new_value?.permissions?.length || 0} access grant{(ev.new_value?.permissions?.length || 0) === 1 ? "" : "s"}
                        </div>
                    )}
                    {ev.action === "role_delete" && (
                        <div className="text-slate-500 italic">
                            deleted (had {ev.old_value?.permissions?.length || 0} access grant{(ev.old_value?.permissions?.length || 0) === 1 ? "" : "s"})
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
}


function ChipList({ label, chips, tone, testidPrefix }) {
    const outerTone = tone === "added"
        ? "text-emerald-700"
        : "text-rose-700";
    const capChipTone = tone === "added"
        ? "bg-emerald-50 text-emerald-800 border-emerald-200"
        : "bg-rose-50 text-rose-800 border-rose-200";
    const uncatTone = "bg-slate-100 text-slate-600 border-slate-200";
    return (
        <div className={`flex items-start gap-1.5 flex-wrap ${outerTone}`}>
            <span className="font-semibold text-xs mt-0.5 min-w-[24px]">{label}</span>
            <div className="flex flex-wrap gap-1">
                {chips.map((c, i) => c.kind === "uncategorised" ? (
                    <span
                        key={`u-${i}`}
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-medium border ${uncatTone}`}
                        title="These access grants pre-date the current capability registry and do not resolve to a business capability."
                        data-testid={`${testidPrefix}-uncat`}
                    >
                        Uncategorised access change ({c.count})
                    </span>
                ) : (
                    <span
                        key={c.identifier}
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-medium border ${capChipTone}`}
                        title={c.module_label
                            ? `${c.module_label}${c.capability_type ? " · " + c.capability_type : ""}${c.risk_level ? " · " + c.risk_level : ""}${c.partial ? " · partial grant" : ""}`
                            : undefined}
                        data-testid={`${testidPrefix}-${c.identifier}`}
                    >
                        {c.danger && "⚠ "}
                        {c.business_name}
                        {c.partial && <span className="ml-1 opacity-70">(partial)</span>}
                    </span>
                ))}
            </div>
        </div>
    );
}
